/**
 * =============================================================================
 * ROASTBREW - HOME PAGE (Content Only)
 * =============================================================================
 * 
 * Main dashboard content with search interface and greeting.
 * This component renders ONLY the main content area - the sidebar is handled
 * by AppLayout which wraps this component.
 * 
 * LAYOUT (within AppLayout):
 * ┌─────────────────────────────────────────────────────────────────────┐
 * │                      [Main Content - This Component]                │
 * │  ┌─────────────────────────────────────────────────────────────┐   │
 * │  │                                                             │   │
 * │  │              Greeting + Search Box                          │   │
 * │  │                                                             │   │
 * │  │              Suggestion Pills                               │   │
 * │  │                                                             │   │
 * │  └─────────────────────────────────────────────────────────────┘   │
 * │                                                                     │
 * │                        Footer Links                                 │
 * └─────────────────────────────────────────────────────────────────────┘
 * 
 * PROPS:
 * @param {object} user - Current user { name, email }
 * @param {string} currentTier - Subscription tier ('free', 'pro', 'pro_plus')
 * @param {object} colors - Theme colors from parent
 * @param {boolean} isMobile - Viewport is mobile (passed from AppLayout)
 * @param {boolean} isTablet - Viewport is tablet (passed from AppLayout)
 * @param {boolean} isDesktop - Viewport is desktop (passed from AppLayout)
 * @param {boolean} sidebarOpen - Sidebar state (passed from AppLayout)
 * 
 * CONNECTED TO:
 * - App.jsx: Parent that provides props
 * - AppLayout.jsx: Layout wrapper that provides responsive props
 * 
 * =============================================================================
 */

import React from 'react';
import { useTheme } from './ThemeContext';
import { useAuth } from './AuthContext';
import SearchBar from './SearchBar';

// Deploy marker — auto-stamped from the build env (see build command).
console.log('[HomePage] build:', process.env.REACT_APP_BUILD_TIME || 'local-dev');


// =============================================================================
// MAIN COMPONENT
// =============================================================================

export default function HomePage({ 
  // Responsive props passed from AppLayout
  isMobile = false,
  isTablet = false,
  isDesktop = true,
  sidebarOpen = true,
}) {
  // ---------------------------------------------------------------------------
  // LOCAL STATE
  // ---------------------------------------------------------------------------
  

  // ---------------------------------------------------------------------------
  // COMPUTED VALUES
  // ---------------------------------------------------------------------------
  
  const { colors } = useTheme();
  const { profile } = useAuth();
  const user = profile ? { name: profile.name, email: profile.email } : null;

  // ---------------------------------------------------------------------------
  // STATIC DATA
  // ---------------------------------------------------------------------------
  

  // ---------------------------------------------------------------------------
  // EVENT HANDLERS
  // ---------------------------------------------------------------------------
  

  // ---------------------------------------------------------------------------
  // RENDER
  // ---------------------------------------------------------------------------
  
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        width: '100%',
        maxWidth: '100%',
        padding: isMobile ? '80px 16px 80px' : isTablet ? '80px 32px 80px' : '48px 48px 80px',
        position: 'relative',
        boxSizing: 'border-box',
        overflowX: 'hidden',
        background: colors.bg,
      }}
    >
      {/* Central Search Container */}
      <div 
        style={{
          width: '100%',
          maxWidth: '720px',
          animation: 'fadeInUp 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        {/* =============================================================
            GREETING SECTION
            Time-aware welcome message with user name
            ============================================================= */}
        <div 
          style={{
            textAlign: 'center',
            marginBottom: isMobile ? '32px' : '48px',
          }}
        >
          <h1 
            style={{
              fontSize: isMobile ? 'clamp(28px, 8vw, 36px)' : 'clamp(36px, 6vw, 56px)',
              fontWeight: '300',
              color: colors.text,
              marginBottom: '16px',
              letterSpacing: '-0.02em',
              lineHeight: 1.1,
              fontFamily: '"Cormorant Garamond", Georgia, serif',
            }}
          >
            {/* Time-based greeting */}
            Good {new Date().getHours() < 12 ? 'morning' : new Date().getHours() < 18 ? 'afternoon' : 'evening'},
            <br />
            <span style={{ fontStyle: 'italic', fontWeight: '400' }}>{user?.name}</span>
          </h1>
          <p 
            style={{
              fontSize: isMobile ? '15px' : '17px',
              color: colors.muted,
              fontFamily: '"DM Sans", sans-serif',
              lineHeight: 1.6,
            }}
          >
            What would you like to learn?
          </p>
        </div>

        {/* Lesson search */}
        <SearchBar variant="home" placeholder="Search courses, lessons, topics..." autoFocus />
      </div>

      {/* =============================================================
          FOOTER LINKS
          Help, Privacy, Terms - dynamically centered based on sidebar state
          ============================================================= */}
      <div 
        style={{
          position: 'fixed',
          bottom: '24px',
          left: isDesktop && sidebarOpen ? 'calc(50% + 140px)' : '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          gap: '24px',
          fontSize: '12px',
          color: colors.muted,
          fontFamily: '"DM Sans", sans-serif',
          zIndex: 10,
          transition: 'left 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        <a 
          href="#" 
          style={{ color: 'inherit', textDecoration: 'none', transition: 'color 0.2s' }}
          onMouseEnter={(e) => e.target.style.color = colors.text}
          onMouseLeave={(e) => e.target.style.color = colors.muted}
        >
          Help
        </a>
        <a 
          href="#" 
          style={{ color: 'inherit', textDecoration: 'none', transition: 'color 0.2s' }}
          onMouseEnter={(e) => e.target.style.color = colors.text}
          onMouseLeave={(e) => e.target.style.color = colors.muted}
        >
          Privacy
        </a>
        <a 
          href="#" 
          style={{ color: 'inherit', textDecoration: 'none', transition: 'color 0.2s' }}
          onMouseEnter={(e) => e.target.style.color = colors.text}
          onMouseLeave={(e) => e.target.style.color = colors.muted}
        >
          Terms
        </a>
      </div>

      {/* =============================================================
          COMPONENT-SPECIFIC STYLES
          ============================================================= */}
      <style>{`
        /* Textarea placeholder color */
        textarea::placeholder {
          color: ${colors.muted};
          transition: opacity 0.2s ease;
        }
        
        /* Hide placeholder when focused */
        textarea:focus::placeholder {
          opacity: 0;
        }
      `}</style>
    </div>
  );
}
